import { useCallback, useRef } from "react";
import { useDispatch, useStore } from "react-redux";
import {
  fetchDashboardOverview,
  patchOverviewAfterContactProcessingSocket,
  patchOverviewAfterRequestAnalysisSocket,
} from "../store/slices/dashboardSlice";
import { pushToast } from "../store/slices/toastSlice";
import {
  applyProcessingUpdateFromSocket,
  applyContactMessageClientEmailJobUpdated,
  applyContactMessageEmailAnalysisFromSocket,
  clearContactMessageClientEmailOutbound,
  fetchContactMessagesList,
  mergeContactMessageEmailMessage,
} from "../store/slices/contactMessagesSlice";
import {
  applyAnalysisUpdateFromSocket,
  applyServiceRequestClientEmailJobUpdated,
  applyServiceRequestEmailAnalysisFromSocket,
  clearServiceRequestClientEmailOutbound,
  fetchServiceRequestsList,
  mergeServiceRequestEmailMessage,
} from "../store/slices/serviceRequestsSlice";
import { aiBucketFromAnalysis } from "../utils/dashboardOverviewSync";
import { useSocketEvent } from "./useSocketEvent";

/**
 * Maps backend Socket.io events to Redux. Keeps handlers stable via refs (no extra re-renders).
 * @param {{ enabled: boolean }} props — false when signed out or admin API off; must match `SocketProvider` `active`.
 */
export default function SocketReduxBridge({ enabled }) {
  const dispatch = useDispatch();
  const store = useStore();

  const storeRef = useRef(store);
  storeRef.current = store;

  /** Avoid duplicate error toasts when both `job:updated` and `emailSendFailed` fire. */
  const emailSendFailToastRef = useRef({ jobId: null, at: 0 });
  const toastEmailSendFailedOnce = useCallback(
    (jobId, message) => {
      const jid = jobId != null ? String(jobId) : "";
      const now = Date.now();
      const prev = emailSendFailToastRef.current;
      if (prev.jobId === jid && jid && now - prev.at < 4000) return;
      emailSendFailToastRef.current = { jobId: jid, at: now };
      dispatch(
        pushToast({
          variant: "error",
          title: "Email not sent",
          message: String(message || "Delivery failed."),
        })
      );
    },
    [dispatch]
  );

  const refetchLists = useCallback(() => {
    if (!enabled) return;
    const st = storeRef.current.getState();
    dispatch(fetchServiceRequestsList(st.serviceRequests.lastListParams || {}));
    dispatch(fetchContactMessagesList(st.contactMessages.lastListParams || {}));
    dispatch(fetchDashboardOverview());
  }, [dispatch, enabled]);

  const onAnalysisUpdated = useCallback(
    (payload) => {
      if (!enabled || !payload?.id || !payload.aiAnalysis) return;
      const sid = String(payload.id);
      const st = storeRef.current.getState();
      const row =
        st.serviceRequests.detail?.id != null && String(st.serviceRequests.detail.id) === sid
          ? st.serviceRequests.detail
          : st.serviceRequests.list.items.find((r) => String(r.id) === sid);
      const fromBucket = aiBucketFromAnalysis(row?.aiAnalysis);
      const toBucket = aiBucketFromAnalysis(payload.aiAnalysis);
      dispatch(
        applyAnalysisUpdateFromSocket({
          id: payload.id,
          aiAnalysis: payload.aiAnalysis,
        })
      );
      dispatch(
        patchOverviewAfterRequestAnalysisSocket({
          id: payload.id,
          fromBucket,
          toBucket,
          aiAnalysis: payload.aiAnalysis,
        })
      );
    },
    [dispatch, enabled]
  );

  const onContactProcessingUpdated = useCallback(
    (payload) => {
      if (!enabled || !payload?.id || !payload.processing) return;
      dispatch(
        applyProcessingUpdateFromSocket({
          id: payload.id,
          processing: payload.processing,
        })
      );
      dispatch(
        patchOverviewAfterContactProcessingSocket({
          id: payload.id,
          processing: payload.processing,
        })
      );
    },
    [dispatch, enabled]
  );

  const onJobUpdated = useCallback(
    (payload) => {
      if (!enabled) return;
      const st = storeRef.current.getState();
      const fail = String(payload?.jobStatus || "").toLowerCase() === "failed";
      const sendMail = String(payload?.type || "").toLowerCase() === "send_client_email";
      const sr = st.serviceRequests.clientEmailOutbound;
      const ct = st.contactMessages.clientEmailOutbound;
      const srMatch =
        fail &&
        sendMail &&
        sr &&
        String(sr.jobId) === String(payload?.jobId) &&
        st.serviceRequests.detail &&
        String(st.serviceRequests.detail.id) === String(payload?.resourceId);
      const ctMatch =
        fail &&
        sendMail &&
        ct &&
        String(ct.jobId) === String(payload?.jobId) &&
        st.contactMessages.detail &&
        String(st.contactMessages.detail.id) === String(payload?.resourceId);

      dispatch(applyServiceRequestClientEmailJobUpdated(payload));
      dispatch(applyContactMessageClientEmailJobUpdated(payload));

      if ((srMatch || ctMatch) && payload?.error) {
        toastEmailSendFailedOnce(payload.jobId, payload.error);
      }
      refetchLists();
    },
    [dispatch, enabled, refetchLists, toastEmailSendFailedOnce]
  );

  const onServiceRequestEmailReceived = useCallback(
    (payload) => {
      if (!enabled || !payload?.id || !payload?.message) return;
      dispatch(
        mergeServiceRequestEmailMessage({ id: payload.id, message: payload.message })
      );
    },
    [dispatch, enabled]
  );

  const onServiceRequestEmailSent = useCallback(
    (payload) => {
      if (!enabled || !payload?.id || !payload?.message) return;
      dispatch(
        mergeServiceRequestEmailMessage({ id: payload.id, message: payload.message })
      );
      dispatch(
        pushToast({
          variant: "success",
          title: "Email sent",
          message: "Your message was delivered.",
        })
      );
    },
    [dispatch, enabled]
  );

  const onServiceRequestEmailSendFailed = useCallback(
    (payload) => {
      if (!enabled || !payload?.id) return;
      dispatch(
        clearServiceRequestClientEmailOutbound({
          parentId: payload.id,
          jobId: payload.jobId,
        })
      );
      dispatch(
        pushToast({
          variant: "error",
          title: "Email not sent",
          message: String(payload.error || "Delivery failed."),
        })
      );
    },
    [dispatch, enabled]
  );

  const onServiceRequestEmailAnalysisUpdated = useCallback(
    (payload) => {
      if (!enabled || !payload?.id || payload?.emailMessageId == null) return;
      dispatch(
        applyServiceRequestEmailAnalysisFromSocket({
          id: payload.id,
          emailMessageId: payload.emailMessageId,
          clientAnalysis: payload.clientAnalysis,
        })
      );
    },
    [dispatch, enabled]
  );

  const onContactMessageEmailReceived = useCallback(
    (payload) => {
      if (!enabled || !payload?.id || !payload?.message) return;
      dispatch(
        mergeContactMessageEmailMessage({ id: payload.id, message: payload.message })
      );
    },
    [dispatch, enabled]
  );

  const onContactMessageEmailSent = useCallback(
    (payload) => {
      if (!enabled || !payload?.id || !payload?.message) return;
      dispatch(
        mergeContactMessageEmailMessage({ id: payload.id, message: payload.message })
      );
      dispatch(
        pushToast({
          variant: "success",
          title: "Email sent",
          message: "Your message was delivered.",
        })
      );
    },
    [dispatch, enabled]
  );

  const onContactMessageEmailSendFailed = useCallback(
    (payload) => {
      if (!enabled || !payload?.id) return;
      dispatch(
        clearContactMessageClientEmailOutbound({
          parentId: payload.id,
          jobId: payload.jobId,
        })
      );
      toastEmailSendFailedOnce(payload.jobId, payload.error);
    },
    [dispatch, enabled, toastEmailSendFailedOnce]
  );

  const onContactMessageEmailAnalysisUpdated = useCallback(
    (payload) => {
      if (!enabled || !payload?.id || payload?.emailMessageId == null) return;
      dispatch(
        applyContactMessageEmailAnalysisFromSocket({
          id: payload.id,
          emailMessageId: payload.emailMessageId,
          clientAnalysis: payload.clientAnalysis,
        })
      );
    },
    [dispatch, enabled]
  );

  useSocketEvent(enabled ? "serviceRequest:analysisUpdated" : null, onAnalysisUpdated);
  useSocketEvent(enabled ? "contactMessage:processingUpdated" : null, onContactProcessingUpdated);
  useSocketEvent(enabled ? "job:updated" : null, onJobUpdated);
  useSocketEvent(enabled ? "serviceRequest:emailReceived" : null, onServiceRequestEmailReceived);
  useSocketEvent(enabled ? "serviceRequest:emailSent" : null, onServiceRequestEmailSent);
  useSocketEvent(enabled ? "serviceRequest:emailSendFailed" : null, onServiceRequestEmailSendFailed);
  useSocketEvent(
    enabled ? "serviceRequest:emailAnalysisUpdated" : null,
    onServiceRequestEmailAnalysisUpdated
  );
  useSocketEvent(enabled ? "contactMessage:emailReceived" : null, onContactMessageEmailReceived);
  useSocketEvent(enabled ? "contactMessage:emailSent" : null, onContactMessageEmailSent);
  useSocketEvent(enabled ? "contactMessage:emailSendFailed" : null, onContactMessageEmailSendFailed);
  useSocketEvent(
    enabled ? "contactMessage:emailAnalysisUpdated" : null,
    onContactMessageEmailAnalysisUpdated
  );

  return null;
}
